import { clients } from 'lib/https';
import { getConfigurationSettings } from 'services/configuration-service';

/** blocks-agents `/agents/soul`: an agent's SOUL.md, who it is, how it sounds and what it values. */

/** `off` = souls are disabled for the tenant; `platform_buddy` = Buddy's soul, which ships with
 *  the platform; `agent` = the agent's own soul; `platform_default` = Klax's default soul. */
export type AgentSoulSource = 'off' | 'platform_buddy' | 'agent' | 'platform_default';

export const AGENT_SOUL_CHAR_LIMIT = 8000;

export interface AgentSoul {
  agentId: string;
  soulsEnabled: boolean;
  source: AgentSoulSource;
  /** What the agent reads now: its own soul as stored, or the platform text. */
  content: string;
  /** The own soul's revision; null while the agent has none. */
  revision: number | null;
  editable: boolean;
  /** True while the content is the untouched creation template. */
  seeded: boolean;
  charLimit: number;
}

export interface UpdateAgentSoulPayload {
  agentId: string;
  /** The full new soul; an empty string removes the agent's own soul. */
  content: string;
  /** The revision the edit is based on; null when the agent has no soul of its own yet. */
  expectedRevision: number | null;
}

export interface UpdateAgentSoulResponse {
  revision: number | null;
  source: AgentSoulSource;
}

type HttpLikeError = { status?: number; error?: { detail?: unknown; currentRevision?: unknown } };

export const soulErrorStatus = (error: unknown) =>
  error && typeof error === 'object' && 'status' in error
    ? Number((error as HttpLikeError).status)
    : undefined;

const soulErrorDetail = (error: unknown) => {
  const detail = (error as HttpLikeError | null)?.error?.detail;
  return typeof detail === 'string' ? detail : '';
};

/**
 * A 404 that does not come from the soul routes: a backend without them answers FastAPI's own
 * `{"detail": "Not Found"}`. The routes themselves answer 404 only with "Agent not found".
 */
export const isSoulRouteMissing = (error: unknown) =>
  soulErrorStatus(error) === 404 && soulErrorDetail(error) !== 'Agent not found';

/** The caller may not see or change this agent's soul (403, or 404 "Agent not found"). */
export const isSoulAccessDenied = (error: unknown) => {
  const status = soulErrorStatus(error);
  return status === 403 || (status === 404 && !isSoulRouteMissing(error));
};

/** The backend refused the content as too long (its other 400 is for control characters). */
export const isSoulTooLongError = (error: unknown) =>
  soulErrorStatus(error) === 400 && !/control characters/i.test(soulErrorDetail(error));

export const soulConflictRevision = (error: unknown): number | null | undefined => {
  if (soulErrorStatus(error) !== 409) return undefined;
  const revision = (error as HttpLikeError).error?.currentRevision;
  return typeof revision === 'number' ? revision : null;
};

/** Counted in code points, as the backend counts. */
export const soulLength = (content: string) => Array.from(content).length;

export const getAgentSoul = async (agentId: string): Promise<AgentSoul> => {
  const config = await getConfigurationSettings();
  const { seliseAiBaseUrl, seliseAi } = config.paths;
  return clients.get<AgentSoul>(
    `${seliseAiBaseUrl}${seliseAi.agentSoul}/${encodeURIComponent(agentId)}`,
    config.blocksKey
  );
};

export const updateAgentSoul = async (
  payload: UpdateAgentSoulPayload
): Promise<UpdateAgentSoulResponse> => {
  const config = await getConfigurationSettings();
  const { seliseAiBaseUrl, seliseAi } = config.paths;
  return clients.post<UpdateAgentSoulResponse>(
    `${seliseAiBaseUrl}${seliseAi.updateAgentSoul}`,
    config.blocksKey,
    JSON.stringify(payload)
  );
};
